import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { ActionButtons } from '@interstate/components/ActionButtons';
import { Button } from '@interstate/components/Button';
import { XMarkIcon } from '@interstate/components/Icons/XMarkIcon';
import { Spinner } from '@interstate/components/Spinner';

import { hideRecentDeals } from '../../store';
import { DOCKED_PANEL_CLOSE_LABEL, DOCKED_PANEL_LABEL, DOCKED_PANEL_OFFSET_VAR, DOCKED_PANEL_WIDTH } from '../../constants';
import { StyledWrapper } from '../RecentDealsList/RecentDealsList.style';
import { DockedBody, DockedFilter, DockedFooter, DockedHeader, DockedPanel, DockedTitle } from './RecentDealsDocked.style';
import type { RecentDealsSlideoutProps } from '../../types/RecentDealsProps';

const RecentDealsDocked: React.FC<RecentDealsSlideoutProps> = ({ isLoading, header, filter, footer, children }) => {
    const dispatch = useDispatch();
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);

    const handleHideDeals = useCallback(() => {
        dispatch(hideRecentDeals());
    }, [dispatch]);

    const handlePanelRef = useCallback((panel: HTMLElement | null) => {
        if (panel) {
            document.documentElement.style.setProperty(DOCKED_PANEL_OFFSET_VAR, DOCKED_PANEL_WIDTH);
            previouslyFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            panel.focus({ preventScroll: true });
        } else {
            document.documentElement.style.removeProperty(DOCKED_PANEL_OFFSET_VAR);
            previouslyFocusedRef.current?.focus();
        }
    }, []);

    return (
        <DockedPanel
            ref={handlePanelRef}
            tabIndex={-1}
            aria-label={DOCKED_PANEL_LABEL}
            data-testid="recent-deals-docked"
            className="recent-deals-docked"
        >
            <DockedHeader>
                <DockedTitle>{header}</DockedTitle>
                <Button
                    buttonStyle="tertiary"
                    size="small"
                    aria-label={DOCKED_PANEL_CLOSE_LABEL}
                    data-testid="recent-deals-docked-close"
                    icon={<XMarkIcon />}
                    onClick={handleHideDeals}
                />
            </DockedHeader>
            {filter && <DockedFilter>{filter}</DockedFilter>}
            <DockedBody>
                {isLoading ? (
                    <StyledWrapper>
                        <Spinner />
                    </StyledWrapper>
                ) : (
                    children
                )}
            </DockedBody>
            <DockedFooter>
                <ActionButtons layout={footer.layout} primary={footer.primary} options={footer.options} />
            </DockedFooter>
        </DockedPanel>
    );
};

export default RecentDealsDocked;
